type UploadedCourseFileAsset = {
  name?: string | null;
  uri?: string | null;
  file?: Blob | null;
};

export type UploadedCourseMetadata = {
  courseName?: string;
  courseCode?: string;
};

const IMS_MANIFEST_FILE_NAME = "imsmanifest.xml";
const ZIP_LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;
const ZIP_CENTRAL_DIRECTORY_FILE_HEADER_SIGNATURE = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const ZIP_STORED_COMPRESSION_METHOD = 0;
const ZIP_DEFLATE_COMPRESSION_METHOD = 8;
const ZIP_MAX_END_OF_CENTRAL_DIRECTORY_SEARCH_BYTES = 65_557;
const COURSE_CODE_PATTERN = /\b[A-Z]{2,}[ -]?\d{2,4}[A-Z]?\b/i;

function getFileNameWithoutExtension(fileName: string | null | undefined): string {
  return (fileName ?? "").replace(/\.[^/.]+$/, "").trim();
}

function getFileExtension(fileName: string | null | undefined): string {
  return fileName?.split(".").pop()?.toLowerCase() ?? "";
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function normalizeCourseCode(value: string): string {
  return value
    .replace(/\s*-\s*/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function inferCourseCodeFromText(value: string | null | undefined): string | undefined {
  const match = normalizeText(value).match(COURSE_CODE_PATTERN)?.[0];
  return match ? normalizeCourseCode(match) : undefined;
}

function removeCourseCodePrefix(title: string, courseCode: string | undefined): string {
  if (!courseCode) return title;

  const escapedCourseCode = courseCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/[ -]/g, "[ -]?");
  return title.replace(new RegExp(`^\\s*${escapedCourseCode}\\s*[:\\-–—|]*\\s*`, "i"), "").trim();
}

function getElementsByLocalName(parent: ParentNode, localName: string): Element[] {
  return Array.from(parent.querySelectorAll("*")).filter((element) => element.localName === localName);
}

function getFirstChildElementByLocalName(parent: ParentNode, localName: string): Element | undefined {
  return Array.from(parent.children).find((element) => element.localName === localName);
}

function getElementText(element: Element | undefined): string | undefined {
  const text = normalizeText(element?.textContent);
  return text || undefined;
}

function getManifestTitle(document: XMLDocument): string | undefined {
  const organizationTitle = getElementsByLocalName(document, "organization")
    .map((organization) => getElementText(getFirstChildElementByLocalName(organization, "title")))
    .find(Boolean);
  if (organizationTitle) return organizationTitle;

  const lomTitle = getElementsByLocalName(document, "general")
    .map((general) => getFirstChildElementByLocalName(general, "title"))
    .map((title) => getElementText(getFirstChildElementByLocalName(title, "string")) ?? getElementText(title))
    .find(Boolean);
  if (lomTitle) return lomTitle;

  return getElementsByLocalName(document, "title").map(getElementText).find(Boolean);
}

function getManifestCourseCode(document: XMLDocument, title: string | undefined): string | undefined {
  const catalogEntryCode = getElementsByLocalName(document, "catalogentry")
    .map((catalogEntry) => getFirstChildElementByLocalName(catalogEntry, "entry"))
    .map((entry) => getElementText(getFirstChildElementByLocalName(entry, "string")) ?? getElementText(entry))
    .map(inferCourseCodeFromText)
    .find(Boolean);
  if (catalogEntryCode) return catalogEntryCode;

  return inferCourseCodeFromText(title) ?? inferCourseCodeFromText(document.documentElement.getAttribute("identifier"));
}

async function getAssetArrayBuffer(asset: UploadedCourseFileAsset): Promise<ArrayBuffer | null> {
  if (asset.file) {
    return asset.file.arrayBuffer();
  }

  if (!asset.uri) {
    return null;
  }

  const response = await fetch(asset.uri);
  return response.arrayBuffer();
}

function findEndOfCentralDirectory(view: DataView): number | null {
  const minimumOffset = Math.max(0, view.byteLength - ZIP_MAX_END_OF_CENTRAL_DIRECTORY_SEARCH_BYTES);
  for (let offset = view.byteLength - 22; offset >= minimumOffset; offset -= 1) {
    if (view.getUint32(offset, true) === ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE) {
      return offset;
    }
  }

  return null;
}

async function inflateRawDeflate(data: Uint8Array): Promise<Uint8Array | null> {
  if (typeof DecompressionStream === "undefined") {
    return null;
  }

  try {
    const blobData = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
    const stream = new Blob([blobData]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

async function extractZipEntryText(arrayBuffer: ArrayBuffer, entryName: string): Promise<string | null> {
  const view = new DataView(arrayBuffer);
  const endOfCentralDirectoryOffset = findEndOfCentralDirectory(view);
  if (endOfCentralDirectoryOffset === null) return null;

  const centralDirectoryRecordCount = view.getUint16(endOfCentralDirectoryOffset + 10, true);
  const centralDirectoryOffset = view.getUint32(endOfCentralDirectoryOffset + 16, true);
  let offset = centralDirectoryOffset;
  const decoder = new TextDecoder();

  for (let index = 0; index < centralDirectoryRecordCount; index += 1) {
    if (view.getUint32(offset, true) !== ZIP_CENTRAL_DIRECTORY_FILE_HEADER_SIGNATURE) break;

    const compressionMethod = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const fileNameLength = view.getUint16(offset + 28, true);
    const extraFieldLength = view.getUint16(offset + 30, true);
    const fileCommentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const fileName = decoder.decode(new Uint8Array(arrayBuffer, offset + 46, fileNameLength));

    if (fileName.toLowerCase() === entryName.toLowerCase()) {
      if (view.getUint32(localHeaderOffset, true) !== ZIP_LOCAL_FILE_HEADER_SIGNATURE) return null;

      const localFileNameLength = view.getUint16(localHeaderOffset + 26, true);
      const localExtraFieldLength = view.getUint16(localHeaderOffset + 28, true);
      const dataOffset = localHeaderOffset + 30 + localFileNameLength + localExtraFieldLength;
      const compressedData = new Uint8Array(arrayBuffer, dataOffset, compressedSize);

      if (compressionMethod === ZIP_STORED_COMPRESSION_METHOD) {
        return decoder.decode(compressedData);
      }

      if (compressionMethod === ZIP_DEFLATE_COMPRESSION_METHOD) {
        const inflatedData = await inflateRawDeflate(compressedData);
        return inflatedData ? decoder.decode(inflatedData) : null;
      }

      return null;
    }

    offset += 46 + fileNameLength + extraFieldLength + fileCommentLength;
  }

  return null;
}

function parseManifestMetadata(manifestXml: string): UploadedCourseMetadata {
  const document = new DOMParser().parseFromString(manifestXml, "application/xml");
  if (document.querySelector("parsererror")) {
    return {};
  }

  const manifestTitle = getManifestTitle(document);
  const courseCode = getManifestCourseCode(document, manifestTitle);
  const courseName = manifestTitle ? removeCourseCodePrefix(manifestTitle, courseCode) || manifestTitle : undefined;

  return {
    courseName,
    courseCode,
  };
}

export async function inferUploadedCourseMetadata(asset: UploadedCourseFileAsset): Promise<UploadedCourseMetadata> {
  const fileName = asset.name ?? "";
  const fallbackName = getFileNameWithoutExtension(fileName);
  const fallbackCode = inferCourseCodeFromText(fallbackName);

  if (getFileExtension(fileName) !== "imscc") {
    return {
      courseName: fallbackName || undefined,
      courseCode: fallbackCode,
    };
  }

  try {
    const arrayBuffer = await getAssetArrayBuffer(asset);
    if (!arrayBuffer) {
      return { courseName: fallbackName || undefined, courseCode: fallbackCode };
    }

    const manifestXml = await extractZipEntryText(arrayBuffer, IMS_MANIFEST_FILE_NAME);
    if (!manifestXml) {
      return { courseName: fallbackName || undefined, courseCode: fallbackCode };
    }

    const manifestMetadata = parseManifestMetadata(manifestXml);
    return {
      courseName: manifestMetadata.courseName ?? fallbackName ?? undefined,
      courseCode: manifestMetadata.courseCode ?? fallbackCode,
    };
  } catch {
    return {
      courseName: fallbackName || undefined,
      courseCode: fallbackCode,
    };
  }
}
